import { PageBanner, PlaceholderArt, Section, SectionTitle, useApi, useDocument } from '@nanning/ui';
import { assetUrl, useT } from '../i18n';
import { creditOf, findImage } from '../useSiteData';
import {} from './common';
import type { ImageryDoc, LeadershipDoc, TeamApi } from '../types';

export function Leadership() {
  const { t, pick } = useT();
  const lead = useDocument<LeadershipDoc>('group', 'leadership.json');
  const team = useApi<TeamApi>('/api/group/team');
  const imagery = useDocument<ImageryDoc>('group', 'imagery.json');

  const error = lead.error ?? team.error ?? imagery.error;
  if (error) {
    return <div className="mx-auto max-w-6xl px-5 py-24 text-danger-light">{error}</div>;
  }
  if (!lead.data || !team.data || !imagery.data) {
    return <div className="mx-auto max-w-6xl px-5 py-24 text-muted">{t('state.loading')}</div>;
  }

  const doc = lead.data.message;
  const members = team.data.team;
  const signatory = members.find((m) => m.id === doc.signatoryId) ?? members[0];
  const img = findImage(imagery.data, 'hero');

  return (
    <>
      <PageBanner
        image={assetUrl(img.local)}
        imageAlt={pick(img.alt)}
        imageCredit={creditOf(img)}
        eyebrow={t('lead.eyebrow')}
        title={pick(doc.title)}
        lead={t('lead.banner')}
      />

      <Section id="letter">
        <div className="grid gap-12 md:grid-cols-[1fr_2fr] md:items-start">
          <figure>
            <PlaceholderArt label={t('lead.portrait')} className="aspect-[3/4] w-full" />
            {signatory && (
              <figcaption className="mt-4">
                <div className="mt-1 text-sm text-muted">{pick(signatory.role)}</div>
              </figcaption>
            )}
          </figure>
          <div className="space-y-6 text-lg leading-relaxed text-body">
            {doc.paragraphs.map((p, i) => (
              <p key={i}>{pick(p)}</p>
            ))}
            {signatory && (
              <p className="pt-4 text-base font-semibold text-body">
                {pick(signatory.name)}
                <span className="block text-sm font-normal text-muted">{pick(signatory.role)}</span>
              </p>
            )}
          </div>
        </div>
      </Section>

      <Section id="team" tone="surface">
        <SectionTitle id="team" title={t('lead.team.title')} lead={t('lead.team.lead')} />
        <ul className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {members.map((m) => (
            <li key={m.id} className="flex flex-col rounded-xl border border-line bg-bg p-6">
              <div className="flex items-center gap-4">
                <span
                  aria-hidden="true"
                  className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-primary/15 text-lg font-semibold text-primary-light"
                >
                  {pick(m.name).slice(0, 1)}
                </span>
                <div>
                  <div className="text-lg font-semibold text-body">{pick(m.name)}</div>
                  <div className="text-sm text-muted">{pick(m.role)}</div>
                </div>
              </div>
              <p className="mt-5 text-sm leading-relaxed text-muted">{pick(m.bio)}</p>
            </li>
          ))}
        </ul>
      </Section>
    </>
  );
}
